import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useAuth } from '../context/AuthContext'
import api from '../api/axios'
import { Plus, X, ChevronDown, Clock, Calendar, Loader2, Pencil, Trash2 } from 'lucide-react'

const LEAVE_TYPES = [
  { value: 'casual', label: 'Casual Leave' },
  { value: 'sick', label: 'Sick Leave' },
  { value: 'half_day', label: 'Half Day' },
  { value: 'permission', label: 'Permission' },
]

const STATUS_STYLES: Record<string, string> = {
  pending: 'bg-yellow-50 text-yellow-700 border border-yellow-200',
  approved: 'bg-green-50 text-green-700 border border-green-200',
  rejected: 'bg-red-50 text-red-700 border border-red-200',
}

const TYPE_STYLES: Record<string, string> = {
  casual: 'bg-blue-50 text-blue-700',
  sick: 'bg-purple-50 text-purple-700',
  half_day: 'bg-teal-50 text-teal-700',
  permission: 'bg-orange-50 text-orange-700',
}

function formatDate(date: string) {
  return new Date(date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

function formatDateTime(date: string) {
  return new Date(date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

// ── Apply Modal ──────────────────────────────────────────────────────────────
function ApplyLeaveModal({ onClose, onSubmitted, editApplication }: { onClose: () => void, onSubmitted: () => void, editApplication?: any }) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])
  const [form, setForm] = useState({
      type: editApplication?.type || 'casual',
      fromDate: editApplication?.fromDate ? new Date(editApplication.fromDate).toISOString().split('T')[0] : '',
      toDate: editApplication?.toDate ? new Date(editApplication.toDate).toISOString().split('T')[0] : '',
      permissionFromTime: editApplication?.permissionFromTime || '',
      permissionToTime: editApplication?.permissionToTime || '',
      halfDaySession: editApplication?.halfDaySession || '',
      reason: editApplication?.reason || ''
    })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const set = (key: string, value: string) => setForm(f => ({ ...f, [key]: value }))
  const isPermission = form.type === 'permission'
  const isHalfDay = form.type === 'half_day'

  const handleSubmit = async () => {
    setError('')
    if (!form.fromDate) return setError('Please select a date')
    if (!form.reason.trim()) return setError('Please enter a reason')
    if (isPermission && (!form.permissionFromTime || !form.permissionToTime)) {
      return setError('Please enter permission from and to time')
    }
    if (isHalfDay && !form.halfDaySession) {
      return setError('Please select a session for half day')
      }
    setSubmitting(true)
    try {
      if (editApplication) {
        await api.patch(`/leave/${editApplication.id}`, {
          type: form.type,
          fromDate: form.fromDate,
          toDate: !isPermission && form.toDate ? form.toDate : null,
          permissionFromTime: isPermission ? form.permissionFromTime : null,
          permissionToTime: isPermission ? form.permissionToTime : null,
          halfDaySession: isHalfDay ? form.halfDaySession : null,
          reason: form.reason
        })
      } else {
        await api.post('/leave', {
          type: form.type,
          fromDate: form.fromDate,
          toDate: !isPermission && form.toDate ? form.toDate : null,
          permissionFromTime: isPermission ? form.permissionFromTime : null,
          permissionToTime: isPermission ? form.permissionToTime : null,
          halfDaySession: isHalfDay ? form.halfDaySession : null,
          reason: form.reason
        })
      }
      onSubmitted()
      onClose()
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to submit application')
    }
    setSubmitting(false)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="bg-white dark:bg-[#1c3a5e] rounded-2xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-display font-bold text-navy-900 text-lg">{editApplication ? 'Edit Leave Application' : 'Apply for Leave'}</h2>
          <button onClick={onClose} className="p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Type */}
          <div>
            <label className="label">Type of Leave</label>
            <div className="relative">
              <select className="input pr-8 appearance-none" value={form.type} onChange={e => set('type', e.target.value)}>
                {LEAVE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {isPermission ? (
            <>
              {/* Permission Date */}
              <div>
                <label className="label">Date</label>
                <input type="date" className="input" value={form.fromDate} onChange={e => set('fromDate', e.target.value)} />
              </div>
              {/* Permission Times */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="label">From Time</label>
                  <input type="time" className="input" value={form.permissionFromTime} onChange={e => set('permissionFromTime', e.target.value)} />
                </div>
                <div>
                  <label className="label">To Time</label>
                  <input type="time" className="input" value={form.permissionToTime} onChange={e => set('permissionToTime', e.target.value)} />
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Leave Dates */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="label">From Date</label>
                  <input type="date" className="input" value={form.fromDate} onChange={e => set('fromDate', e.target.value)} />
                </div>
                <div>
                  <label className="label">To Date <span className="text-gray-400 font-normal">(optional)</span></label>
                  <input type="date" className="input" value={form.toDate} min={form.fromDate} onChange={e => set('toDate', e.target.value)} />
                </div>
              </div>
            </>
          )}

          {isHalfDay && (
            <div>
              <label className="label">Session</label>
              <div className="flex gap-3">
                {[{ value: 'morning', label: '🌅 Morning (First Half)' }, { value: 'afternoon', label: '🌇 Afternoon (Second Half)' }].map(opt => (
                  <label key={opt.value} className="flex items-center gap-2 cursor-pointer flex-1 border rounded-xl px-4 py-2.5 transition-all"
                    style={{ borderColor: form.halfDaySession === opt.value ? '#f97316' : '#e5e7eb', background: form.halfDaySession === opt.value ? '#fff7ed' : '#fff' }}>
                    <input type="radio" name="halfDaySession" value={opt.value}
                      checked={form.halfDaySession === opt.value}
                      onChange={() => set('halfDaySession', opt.value)}
                      className="accent-orange-500" />
                    <span className="text-sm text-navy-700">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="label">Reason</label>
            <textarea className="input resize-none" rows={3} placeholder="Enter reason for leave..." value={form.reason} onChange={e => set('reason', e.target.value)} />
          </div>

          {error && <p className="text-red-500 text-sm">{error}</p>}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={handleSubmit} disabled={submitting} className="btn-primary flex items-center gap-2">
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {editApplication ? 'Update Application' : 'Submit Application'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Review Modal ─────────────────────────────────────────────────────────────
function LeaveDetailModal({ application, canReview, onClose, onReviewed }: {
  application: any, canReview: boolean, onClose: () => void, onReviewed: () => void
}) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])
  const { user } = useAuth()
  const [reviewNote, setReviewNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleReview = async (status: 'approved' | 'rejected') => {
    setSubmitting(true)
    setError('')
    try {
      await api.patch(`/leave/${application.id}/review`, { status, reviewNote })
      onReviewed()
      onClose()
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to review application')
    }
    setSubmitting(false)
  }

  const isPermission = application.type === 'permission'
  const isHalfDay = application.type === 'half_day'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="bg-white dark:bg-[#1c3a5e] rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-display font-bold text-navy-900 text-lg">Leave Application</h2>
          <button onClick={onClose} className="p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Staff info */}
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
            <div className="w-9 h-9 bg-orange-500/20 rounded-lg flex items-center justify-center">
              <span className="text-orange-500 font-bold text-sm">{application.staffName?.[0]}</span>
            </div>
            <div>
              <p className="font-semibold text-navy-900 text-sm">{application.staffName}</p>
              <p className="text-xs text-gray-400">{application.staffRoleLabel}</p>
            </div>
            <span className={`ml-auto px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[application.status]}`}>
              {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
            </span>
          </div>

          {/* Leave details */}
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Type</span>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${TYPE_STYLES[application.type] || 'bg-gray-100 text-gray-600'}`}>
                {LEAVE_TYPES.find(t => t.value === application.type)?.label || application.type}
              </span>
            </div>

            {isPermission ? (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Date</span>
                  <span className="font-medium text-navy-800">{formatDate(application.fromDate)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Timing</span>
                  <span className="font-medium text-navy-800 flex items-center gap-1">
                    <Clock size={12} />
                    {application.permissionFromTime} — {application.permissionToTime}
                              </span>
                            </div>
                          </>
                        ) : isHalfDay ? (
                          <>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-500">Date</span>
                              <span className="font-medium text-navy-800">{formatDate(application.fromDate)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-500">Session</span>
                              <span className="font-medium text-navy-800 capitalize">
                                {application.halfDaySession === 'morning' ? '🌅 Morning (First Half)' : '🌇 Afternoon (Second Half)'}
                              </span>
                            </div>
                          </>
                        ) : (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Date(s)</span>
                <span className="font-medium text-navy-800">
                  {formatDate(application.fromDate)}
                  {application.toDate && application.toDate !== application.fromDate && ` → ${formatDate(application.toDate)}`}
                </span>
              </div>
            )}

            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Submitted</span>
              <span className="text-gray-600">{formatDateTime(application.createdAt)}</span>
            </div>

            <div className="text-sm">
              <span className="text-gray-500">Reason</span>
              <p className="mt-1 text-navy-800 bg-gray-50 rounded-lg p-3 text-sm">{application.reason}</p>
            </div>

            {application.reviewedBy && (
              <div className="text-sm space-y-1 pt-1 border-t border-gray-100">
                <div className="flex justify-between">
                  <span className="text-gray-500">Reviewed by</span>
                  <span className="text-gray-700">{application.reviewedBy}</span>
                </div>
                {application.reviewNote && (
                  <div>
                    <span className="text-gray-500">Note</span>
                    <p className="mt-1 text-navy-800 bg-gray-50 rounded-lg p-3 text-sm">{application.reviewNote}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Review section */}
          {canReview && application.status === 'pending' && application.userId !== user?.userId && (
            <div className="pt-2 border-t border-gray-100 space-y-3">
              <div>
                <label className="label">Review Note <span className="text-gray-400 font-normal">(optional)</span></label>
                <textarea className="input resize-none" rows={2} placeholder="Add a note..." value={reviewNote} onChange={e => setReviewNote(e.target.value)} />
              </div>
              {error && <p className="text-red-500 text-sm">{error}</p>}
              <div className="flex gap-3">
                <button onClick={() => handleReview('rejected')} disabled={submitting}
                  className="flex-1 py-2 rounded-xl border border-red-200 text-red-600 hover:bg-red-50 text-sm font-medium transition-colors">
                  Reject
                </button>
                <button onClick={() => handleReview('approved')} disabled={submitting}
                  className="flex-1 py-2 rounded-xl bg-green-500 hover:bg-green-600 text-white text-sm font-medium transition-colors flex items-center justify-center gap-2">
                  {submitting && <Loader2 size={13} className="animate-spin" />}
                  Approve
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Main Page ────────────────────────────────────────────────────────────────
export default function LeaveApplicationPage() {
  const { user, hasPermission } = useAuth()
  const [applications, setApplications] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showApply, setShowApply] = useState(false)
  const [selected, setSelected] = useState<any>(null)
  const [editingLeave, setEditingLeave] = useState<any>(null)
  const [leavePage, setLeavePage] = useState(1)
  const LEAVE_LIMIT = 10
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterType, setFilterType] = useState('all')
  const [filterDateFrom, setFilterDateFrom] = useState('')
  const [filterDateTo, setFilterDateTo] = useState('')
  const resetLeavePage = () => setLeavePage(1)

  const canReview = hasPermission('approve_leave')
  const canApply = hasPermission('apply_leave')
  const isPrivileged = canReview

  const fetchApplications = async () => {
    setLoading(true)
    try {
      const res = await api.get('/leave')
      setApplications(res.data)
    } catch {}
    setLoading(false)
  }

  useEffect(() => { fetchApplications() }, [])

  const filtered = applications.filter(a => {
    const matchSearch = !search || a.staffName.toLowerCase().includes(search.toLowerCase()) || a.reason.toLowerCase().includes(search.toLowerCase())
    const matchStatus = filterStatus === 'all' || a.status === filterStatus
    const matchType = filterType === 'all' || a.type === filterType
    const appDate = new Date(a.fromDate)
    const matchDateFrom = !filterDateFrom || appDate >= new Date(filterDateFrom)
    const matchDateTo = !filterDateTo || appDate <= new Date(filterDateTo)
    return matchSearch && matchStatus && matchType && matchDateFrom && matchDateTo
  })

  const totalLeavePages = Math.ceil(filtered.length / LEAVE_LIMIT)
  const paginatedLeave = filtered.slice((leavePage - 1) * LEAVE_LIMIT, leavePage * LEAVE_LIMIT)

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display font-bold text-navy-900">Leave Applications</h1>
          <p className="text-sm text-gray-400 mt-0.5">
            {isPrivileged ? 'Manage staff leave applications' : 'Your leave applications'}
          </p>
        </div>
        {canApply && (
          <button onClick={() => setShowApply(true)} className="btn-primary flex items-center gap-2">
            <Plus size={16} />
            Apply
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        {isPrivileged && (
          <input
            type="text"
            placeholder="Search staff or reason..."
            className="input w-56 text-sm"
            value={search}
            onChange={e => { setSearch(e.target.value); setLeavePage(1) }}
          />
        )}
        <select className="input w-36 text-sm" value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setLeavePage(1) }}>
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        <select className="input w-36 text-sm" value={filterType} onChange={e => { setFilterType(e.target.value); setLeavePage(1) }}>
          <option value="all">All Types</option>
          <option value="casual">Casual Leave</option>
          <option value="sick">Sick Leave</option>
          <option value="permission">Permission</option>
        </select>
        <input
          type="date"
          className="input w-40 text-sm"
          value={filterDateFrom}
          onChange={e => { setFilterDateFrom(e.target.value); setLeavePage(1) }}
          placeholder="From date"
        />
        <input
          type="date"
          className="input w-40 text-sm"
          value={filterDateTo}
          onChange={e => { setFilterDateTo(e.target.value); setLeavePage(1) }}
          placeholder="To date"
        />
        <button
          onClick={() => {
            const now = new Date()
            const first = new Date(now.getFullYear(), now.getMonth(), 1)
            const last = new Date(now.getFullYear(), now.getMonth() + 1, 0)
            setFilterDateFrom(first.toISOString().slice(0, 10))
            setFilterDateTo(last.toISOString().slice(0, 10))
          }}
          className="px-3 py-2 rounded-xl bg-orange-50 text-orange-600 hover:bg-orange-100 text-sm font-medium border border-orange-200 transition-colors">
          This Month
        </button>
        {(filterDateFrom || filterDateTo) && (
          <button
            onClick={() => { setFilterDateFrom(''); setFilterDateTo('') }}
            className="px-3 py-2 rounded-xl bg-gray-50 text-gray-500 hover:bg-gray-100 text-sm font-medium border border-gray-200 transition-colors">
            Clear Dates
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-[#16304f] rounded-2xl shadow-sm border border-gray-100 dark:border-[#1e3a5f] overflow-hidden">
        <div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider hidden md:table-cell">Submitted</th>
                {isPrivileged && <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Staff</th>}
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Type</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Date(s)</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider hidden sm:table-cell">Timing</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider hidden sm:table-cell">Reason</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider hidden sm:table-cell">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr><td colSpan={isPrivileged ? 7 : 6} className="py-16 text-center text-gray-400">
                  <Loader2 size={24} className="animate-spin mx-auto mb-2" />
                  Loading...
                </td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={isPrivileged ? 7 : 6} className="py-16 text-center text-gray-400">
                  <Calendar size={32} className="mx-auto mb-2 opacity-30" />
                  No leave applications found
                </td></tr>
              ) : paginatedLeave.map(a => (
                <tr key={a.id} onClick={() => setSelected(a)}
                  className="hover:bg-orange-50/30 cursor-pointer transition-colors">
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap hidden md:table-cell">{formatDateTime(a.createdAt)}</td>
                  {isPrivileged && (
                    <td className="px-4 py-3">
                      <p className="font-medium text-navy-800 dark:text-white">{a.staffName}</p>
                      <p className="text-xs text-gray-400">{a.staffRoleLabel}</p>
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${TYPE_STYLES[a.type] || 'bg-gray-100 text-gray-600'}`}>
                      {LEAVE_TYPES.find(t => t.value === a.type)?.label || a.type}
                    </span>
                    <div className="sm:hidden mt-1">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[a.status]}`}>
                        {a.status.charAt(0).toUpperCase() + a.status.slice(1)}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                    {a.type === 'permission' ? (
                      formatDate(a.fromDate)
                    ) : a.type === 'half_day' ? (
                      <>{formatDate(a.fromDate)} <span className="text-xs text-teal-600 capitalize">({a.halfDaySession})</span></>
                    ) : (
                      <>
                        {formatDate(a.fromDate)}
                        {a.toDate && a.toDate !== a.fromDate && <span className="text-gray-400"> → {formatDate(a.toDate)}</span>}
                      </>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap hidden sm:table-cell">
                    {a.type === 'permission' && a.permissionFromTime ? (
                      <span className="flex items-center gap-1 text-xs">
                        <Clock size={11} />
                        {a.permissionFromTime} — {a.permissionToTime}
                      </span>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-600 max-w-xs truncate hidden sm:table-cell">{a.reason}</td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <div className="flex items-center gap-2">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[a.status]}`}>
                        {a.status.charAt(0).toUpperCase() + a.status.slice(1)}
                      </span>
                      {a.status === 'pending' && !isPrivileged && (
                        <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                          <button onClick={() => setEditingLeave(a)}
                            className="p-1 rounded-lg text-gray-400 hover:text-orange-500 hover:bg-orange-50 transition-colors">
                            <Pencil size={12} />
                          </button>
                          <button onClick={async () => {
                            if (!window.confirm('Cancel this leave application?')) return
                            try {
                              await api.delete(`/leave/${a.id}`)
                              fetchApplications()
                            } catch {}
                          }}
                            className="p-1 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                            <Trash2 size={12} />
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalLeavePages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-gray-400">{filtered.length} applications — page {leavePage} of {totalLeavePages}</p>
          <div className="flex items-center gap-2">
            <button disabled={leavePage === 1} onClick={() => setLeavePage(p => p - 1)}
              className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Previous</button>
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-400">Go to</span>
              <input type="number" min={1} max={totalLeavePages} defaultValue={leavePage} key={leavePage}
                onKeyDown={e => { if (e.key === 'Enter') { const v = parseInt((e.target as HTMLInputElement).value); if (v >= 1 && v <= totalLeavePages) setLeavePage(v) }}}
                onBlur={e => { const v = parseInt(e.target.value); if (v >= 1 && v <= totalLeavePages) setLeavePage(v) }}
                className="w-14 text-xs text-center border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-orange-400" />
              <span className="text-xs text-gray-400">of {totalLeavePages}</span>
            </div>
            <button disabled={leavePage * LEAVE_LIMIT >= filtered.length} onClick={() => setLeavePage(p => p + 1)}
              className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Next</button>
          </div>
        </div>
      )}

      {/* Modals */}
      {showApply && createPortal(<ApplyLeaveModal onClose={() => setShowApply(false)} onSubmitted={fetchApplications} />, document.body)}
      {editingLeave && createPortal(<ApplyLeaveModal onClose={() => setEditingLeave(null)} onSubmitted={() => { fetchApplications(); setEditingLeave(null) }} editApplication={editingLeave} />, document.body)}
      {selected && createPortal(
        <LeaveDetailModal
          application={selected}
          canReview={canReview}
          onClose={() => setSelected(null)}
          onReviewed={() => { fetchApplications(); setSelected(null) }}
        />,
        document.body
      )}
    </div>
  )
}